import { useContext, useMemo } from 'react'

import { type FrameSettings } from '@frame-builder/lib/frame-viewer/settings'
import { clampRange } from '@lib/helpers'
import { getSanityImageUrl } from '@lib/image'
import {
  getPassepartoutHoleSizeFromArtworkSize,
  getSizeDimensionsFromString,
} from '@lib/size'
import { ProductCombinedListingContext } from './context'

/**
 * Hook that returns frame viewer settings based on combined listing selection.
 */
export const useFrameSettings = (
  artworkUrl: string | null,
  backgroundColor: string,
) => {
  const {
    combinedListingProducts,
    combinedListingFrameSize,
    combinedListingArtworkSize,
  } = useContext(ProductCombinedListingContext)

  return useMemo(() => {
    const frameProduct = combinedListingProducts?.find(
      (combinedListingProduct) =>
        combinedListingProduct.combinedListingProductType === 'frame',
    )
    const passepartoutProduct = combinedListingProducts?.find(
      (combinedListingProduct) =>
        combinedListingProduct.combinedListingProductType === 'passepartout',
    )
    const glassProduct = combinedListingProducts?.find(
      (combinedListingProduct) =>
        combinedListingProduct.combinedListingProductType === 'glass',
    )

    const profile = frameProduct?.combinedListingFrameBuilderProfile?.profile
    const texture = frameProduct?.combinedListingFrameBuilderProfile?.texture
    const glass = glassProduct?.combinedListingFrameBuilderGlass

    const [frameWidth, frameHeight] = combinedListingFrameSize
      ? getSizeDimensionsFromString(combinedListingFrameSize)
      : ([,] as const)

    if (!profile || !texture || !frameWidth || !frameHeight) {
      return
    }

    const [artworkWidth, artworkHeight] = combinedListingArtworkSize
      ? getSizeDimensionsFromString(combinedListingArtworkSize)
      : [frameWidth, frameHeight]
    let passepartoutTopSize = 0
    let passepartoutRightSize = 0
    let passepartoutBottomSize = 0
    let passepartoutLeftSize = 0

    const profileWidth = profile.width ?? 0
    const profileThickness = profile.thickness ?? 0
    const glassThickness = glass?.thickness ?? 0
    const passepartoutThickness =
      passepartoutProduct?.combinedListingFrameBuilderPassepartoutColor
        ?.thickness ?? 0

    const passepartoutColor =
      passepartoutProduct?.combinedListingFrameBuilderPassepartoutColor?.color
        ?.hex ?? ''
    const passepartoutCoreColor =
      passepartoutProduct?.combinedListingFrameBuilderPassepartoutColor
        ?.coreColor?.hex

    const textureImage = texture.image

    if (passepartoutProduct) {
      // Get mount cutout size
      const [passepartoutHoleWidth, passepartoutHoleHeight] =
        getPassepartoutHoleSizeFromArtworkSize(artworkWidth, artworkHeight)
      const normalizedPassepartoutHoleWidth = clampRange(
        passepartoutHoleWidth,
        0,
        frameWidth,
      )
      const normalizedPassepartoutHoleHeight = clampRange(
        passepartoutHoleHeight,
        0,
        frameHeight,
      )

      passepartoutTopSize = (frameHeight - normalizedPassepartoutHoleHeight) / 2
      passepartoutRightSize = (frameWidth - normalizedPassepartoutHoleWidth) / 2
      passepartoutBottomSize =
        (frameHeight - normalizedPassepartoutHoleHeight) / 2
      passepartoutLeftSize = (frameWidth - normalizedPassepartoutHoleWidth) / 2
    }

    const frameSettings: FrameSettings = {
      type: 'frame',
      frameDiffuseUrl: getSanityImageUrl(textureImage) ?? '',
      frameTopSize: profileWidth / 1000,
      frameRightSize: profileWidth / 1000,
      frameBottomSize: profileWidth / 1000,
      frameLeftSize: profileWidth / 1000,
      frameDepth: profileThickness / 1000,
      frameInset: glassThickness / 1000,
      passepartoutColor,
      passepartoutCoreColor,
      passepartoutTopSize: passepartoutTopSize / 100,
      passepartoutRightSize: passepartoutRightSize / 100,
      passepartoutBottomSize: passepartoutBottomSize / 100,
      passepartoutLeftSize: passepartoutLeftSize / 100,
      passepartoutDepth: passepartoutThickness / 1000,
      artworkWidth: artworkWidth / 100,
      artworkHeight: artworkHeight / 100,
      backgroundColor,
    }

    if (artworkUrl) {
      frameSettings.artworkUrl = artworkUrl
    }

    return frameSettings
  }, [
    artworkUrl,
    backgroundColor,
    combinedListingArtworkSize,
    combinedListingFrameSize,
    combinedListingProducts,
  ])
}
